'use client';

import { useEffect, useState } from 'react';
import { asText, Content } from '@prismicio/client';

import { toTime } from '@preventx/calcium';
import { Typography } from '@preventx/oxygen';
import { defaultEnv } from '@preventx/prismic-services';

// Types
import { MappedClinicOpenTimeDetails } from '@/types/clinics/clinic';

// Contexts
import { useReusableContentContext } from '@/context/ReusableContentProvider';

// Components
import { RichTextDefault } from '@/components/RichText/RichTextDefault';

type OpeningTimesProps = {
  openingTime: Content.ClinicOpeningTimesSliceDefaultPrimaryOpeningTimesItem;
};

export const OpeningTimes = ({ openingTime }: OpeningTimesProps) => {
  const { reusableContent } = useReusableContentContext();

  const [configuration, setConfiguration] = useState<
    MappedClinicOpenTimeDetails | undefined
  >(undefined);

  useEffect(() => {
    if (!reusableContent) {
      return;
    }

    const mappedLabel = {
      Friday: reusableContent.friday,
      Sunday: reusableContent.sunday,
      Monday: reusableContent.monday,
      Tuesday: reusableContent.tuesday,
      Saturday: reusableContent.saturday,
      Thursday: reusableContent.thursday,
      Wednesday: reusableContent.wednesday,
    }[openingTime.day_of_week];

    const formattedOpenFrom = openingTime.open_from
      ? toTime(openingTime.open_from, defaultEnv.CountryCode)
      : undefined;

    const formattedOpenUntil = openingTime.open_until
      ? toTime(openingTime.open_until, defaultEnv.CountryCode)
      : undefined;

    setConfiguration({
      label: mappedLabel,
      openFrom: formattedOpenFrom,
      openUntil: formattedOpenUntil,
    });
  }, [reusableContent]);

  if (!configuration) {
    return null;
  }

  const { label, openFrom, openUntil } = configuration;

  return (
    <>
      <dt className="key">{asText(label)}</dt>
      <dd className="val">
        {(openFrom || openUntil) && (
          <Typography component="time">
            {openFrom} - {openUntil}
          </Typography>
        )}

        <RichTextDefault content={openingTime.useful_information} />
      </dd>
    </>
  );
};
